import React, { useState, useEffect, useCallback } from 'react';
import useForm from '@utils/useForm';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import { useTheme, ITheme } from 'src/theme';
import { useAvivaSelector, useAvivaDispatch } from '@redux/hook';
import { useHistory } from 'react-router-dom';
import { Helpers } from '@utils/helpers';
import { GAAviva } from '@utils/gaAviva';

import { isEmpty, get, includes, lowerCase, find } from 'lodash';
import { updateIncome, updateHealth, completeStep } from '@redux/slices/SIO/step3Slice';
import { AboutYouForm, AboutYouHealthForm } from '@models/SIO/AboutYou.model';
import { setLoadingState } from '@redux/slices/loadingSlice';
import { EzSaleRequest } from '@models/EzSaleRequest.model';
import { BMICheckRequest } from '@models/BMIRequest.model';
import { Coverages, SQSPocketResponse } from '@models/SQSPocketRequest.model';
import { IMyInfoQuestionRequest } from '@models/MyInfoRequest.model';

import { postCalculateMinAssuredAndFlag, postValidateMaxSumAssuredAndFlag, postBMICheck, postSavingMyInfo } from '@api/index';

import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import MultiSteps from '@components/MultiSteps';
import Heading from '@components/Heading';
import Input from '@components/Input';
import Dropdown from '@components/Dropdown';
import CheckboxList from '@components/CheckboxList';
import Checkbox from '@components/Checkbox';
import Text from '@components/Text';
import Grid from '@components/Grid';
import Cell from '@components/Cell';
import Button from '@components/Button';
import ContactBox from '@components/ContactBox';
import BulletList from '@components/BulletList';
import Bullet from '@components/Bullet';
import Divider from '@components/Divider';
import ContainerBlock from '@components/ContainerBlock';
import RadioButtonGroup from '@components/RadioButtonGroup';
import RadioButton from '@components/RadioButton';
import Modal from '@components/Modal';

import { validate, validateHealthForm } from './service';
import Link from '@components/Link';

const page = 'About You';

const customRadioButton = css`
  @media screen and (max-width: 767px) {
    .radio-item {
      width: 100%;
      &:nth-child(odd) {
        &:after{
          width: 175px;
          height: 1px;
          bottom: 0;
          left: 50%;
          right: unset;
          top: unset;
          transform: translateX(-50%);
        }
      }
    }
    .radio-hover {
      bottom: auto;
      right: 0;
      height: 50%;
      width: auto;
    }
    .radio-hover.o0 {
      transform: translateY(100%);
    }
    .radio-hover.o100 {
      transform: translateY(0);
    }
  }
`

const SmokerStatusContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const smallMargin = css`
  margin: 0 0 5px;
`;
const largeMargin = css`
  margin: 0;
`;
export interface AboutYouProps {
  theme?: ITheme
}

 


const convertHealthQuestions = (values: AboutYouHealthForm, isSmoker: boolean) => {
  let question = `1,${isSmoker ? 'Y' : 'N'}`;//|2,${values['q1']}|3,${values['q2']}|4,${values['q3']}|5,${values['q4']}`;
  question = values['q1'] ? `${question}|2,${values['q1']}` : question;
  question = values['q2'] ? `${question}|3,${values['q2']}` : question;
  question = values['q3'] ? `${question}|4,${values['q3']}` : question;
  question = values['q4'] ? `${question}|5,${values['q4']}` : question;
  if (values['isAppliedCI'] && values['q5']) {
    question = `${question}|6,${values['q5']}`
  }
  if (isSmoker && values['isLessThan40']) {
    question = `${question}|10,${values['isLessThan40']}`
  }
  return `${question}`;
}

const createEZPayload = (
  nric: string,
  simplePlan: Coverages | undefined,
  illnessCoverPlan: Coverages | undefined,
  personalAccidentPlan: Coverages | undefined,
) => {
  const payload = new EzSaleRequest({
    nric,
    campaignCode: 'CQG',
    basicSumAssured: get(simplePlan, 'attributes.sumAssured'),
    bsComponentCode: get(simplePlan, 'attributes.componentCode'),
    criticalIllnessSumAssured: get(illnessCoverPlan, 'attributes.sumAssured'),
    ciComponentCode: get(illnessCoverPlan, 'attributes.componentCode'),
    personalAccidentSumAssured: get(personalAccidentPlan, 'attributes.sumAssured'),
    paComponentCode: get(personalAccidentPlan, 'attributes.componentCode'),
  });
  return payload;
}

const trackOnload = (step: string | number = 0): void => {
  const GA = GAAviva.getInstance();
  let { form } = GA.digitalData;
  form.name = Number(step) === 1 ? 'Health Questions' : 'Income Questions';
  GA.trackPageLoad({ form });
}

const trackFormSubmmit = (error: string = ''): void =>  {
  const GA = GAAviva.getInstance();
  if (isEmpty(error)) {
    GA.trackFormSuccess({});
    return;
  }
  const { form } = GA.digitalData;
  form.error.messages = error;
  GA.trackFormFailed({ form });
}

function AboutYou(props: AboutYouProps) {
  console.debug(`Aviva - ${page}`);
  const theme: ITheme = useTheme();
  const history = useHistory();
  const dispatch = useAvivaDispatch();

  const minStep = 0, maxStep = 1;
  const contactInformation = useAvivaSelector(state => state.staticData.contactInformation);
  const businesses = useAvivaSelector(state => state.staticData.businesses);
  const sourceOfFunds = useAvivaSelector(state => state.staticData.sourceOfFunds);
  const sourceOfWealths = useAvivaSelector(state => state.staticData.sourceOfWealths);
  const basicDetials = useAvivaSelector(state => state.sioStep1.basicDetails);
  const quoteRawResponse = useAvivaSelector(state => state.sioStep1.quoteRawResponse);
  const sqsQuotation = useAvivaSelector(state => state.sioStep1.sqsQuotation);
  const occupationSlice = useAvivaSelector(state => state.sioStep1.occupation);
  const transactionId = useAvivaSelector(state=>state.sioStep1.transactionId)

  const isStep2Completed = useAvivaSelector(state => state.sioStep2.isCompleted);
  const personalDetails = useAvivaSelector(state => state.sioStep2.personalDetails);
  const retrieveMyInfo = useAvivaSelector(state => state.sioStep2.retrievedMyInfo);

  const initialIncome = useAvivaSelector(state => state.sioStep3.income);
  const initialHealth = useAvivaSelector(state => state.sioStep3.health);
  const typeUW = useAvivaSelector(state => state.sioStep3.typeUW);
  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [isSmoker, setIsSmoker] = useState<boolean | undefined>(undefined);
  const [isAppliedCI, setIsAppliedCI] = useState<boolean | undefined>(undefined);
  const [isShowPopup, setIsShowPopup] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [messageAdd1, setMessageAdd1] = useState<string>('');
  const [messageAdd2, setMessageAdd2] = useState<string>('');

  const { values, errors, handleChange, formValid, patchValue } = useForm(new AboutYouForm({ ...initialIncome }), () => { }, validate);

  const { 
    values: healthValues,
    errors: healthErrors,
    handleChange: handleChangeHealth,
    formValid: healthFormValid,
    patchValue: patchHealthValue
  } = useForm(new AboutYouHealthForm({ ...initialHealth }), () => { }, validateHealthForm);

  const [step, setStep] = useState<number>(minStep);

  const urlParams: URLSearchParams = Helpers.getURLSearchParams();

  const togglePopup = () => {
    setIsShowPopup(!isShowPopup);
  }

  const onChangeCheckBox = (item: any, type: string) => (event: any) => {
    const { value } = event.target;
    const currentValue = values[type];
    let nextValue = currentValue ? [...currentValue] : [];
    
    if (value) {
      nextValue.push(item.value);
    } else {
      nextValue = nextValue.filter((o: any) => o !== item.value);
    }
    console.debug('nextValue', nextValue);

    patchValue({ [type]: [...nextValue] });
  }

  const gotoContact = useCallback(() => {
    history.push('/sio/contact');
  }, [history])

  const needToGoToContact = useCallback(() => {
    if (isSmoker && healthValues['isLessThan40'] === 'N') {
      return true;
    }
    const basedDeclinedAnswer = healthValues['q1'] === 'Y' || healthValues['q2'] === 'Y' || healthValues['q3'] === 'Y' || healthValues['q4'] === 'Y' || healthValues['q5'] === 'Y';

    if (basedDeclinedAnswer) {
      return true;
    }

    return false;
  }, [isSmoker, healthValues])

  const updateMyInfoAndContinue = useCallback(async () => {
    const id = retrieveMyInfo?.id || 0;
    const healthQuestions = convertHealthQuestions(healthValues, isSmoker || false);
    const businessNature = Number(values['nature']);
    const exactDutiesOccupation = values['duties'];
    const businessNatureOthers = values['otherBusiness'] || undefined;
    const annualIncome = values['income'];
    const height = Number(healthValues['height']);
    const weight = Number(healthValues['weight']);
    const occupation = occupationSlice?.desc;
    const smokingStatus = isSmoker ? 'Yes' : 'No';

    console.debug('healthValues', healthValues);
    // form wealth data
    let sourceOfWealth = '';
    values['wealth'].forEach((item: string) => {
      const f_item = item === 'other' ? `OTH` : item;
      sourceOfWealth = sourceOfWealth ? `${sourceOfWealth}|${f_item},${item === 'other' ? values['otherWealth'] : 'Y'}` : `${f_item},${item === 'other' ? values['otherWealth'] : 'Y'}`;
    });
    console.debug('sourceOfWealth', sourceOfWealth);

    // form fund data
    let sourceOfFunds = '';
    values['fund'].forEach((item: string) => {
      const f_item = item === 'other' ? `OTH` : item;
      sourceOfFunds = sourceOfFunds ? `${sourceOfFunds}|${f_item},${item === 'other' ? values['otherFund'] : 'Y'}` : `${f_item},${item === 'other' ? values['otherFund'] : 'Y'}`;
    });
    console.debug('sourceOfFunds', sourceOfFunds);

    const payload: IMyInfoQuestionRequest = { id, exactDutiesOccupation, businessNature, businessNatureOthers, sourceOfWealth, sourceOfFunds, healthQuestions, occupation, height, weight, annualIncome, smokingStatus, transactionId };
    try {
      const response = await postSavingMyInfo(payload);
      console.debug('Update my info', { payload, response });
      dispatch(completeStep(true));
      history.push('/sio/declaration');

    } catch (error) {
      console.debug('Update my info', error);
      dispatch(completeStep(true));
      history.push('/sio/declaration'); //bypass first
    }
  }, [retrieveMyInfo, history, healthValues, values, dispatch, isSmoker, occupationSlice]);

  const checkMaxSumAssuredAndFlag = useCallback(async () => {
    const nric = get(personalDetails, 'nric', '');
    const simplePlan: Coverages | undefined = find(sqsQuotation?.coverages, { id: 'simpleTermPlan' });
    const illnessCoverPlan: Coverages | undefined = find(sqsQuotation?.coverages, { id: 'lateStageAddCICover' });
    const personalAccidentPlan: Coverages | undefined = find(sqsQuotation?.coverages, { id: 'personalAccidentRider' });
    const payload = createEZPayload(nric, simplePlan, illnessCoverPlan, personalAccidentPlan);
    
    try {
      const response = await postValidateMaxSumAssuredAndFlag(payload);
      const data = get(response, 'data');
      // const clientSAInfo = get(data, 'data.clientSAInfo');
      const clientSCDPF = get(data, 'data.clientSCDPF');
      const claimFlg = get(clientSCDPF, 'claimFlg', '');
      const substdFlg = get(clientSCDPF, 'subStdFlg', '');
      const postponeFlg = get(clientSCDPF, 'postponeFlg', '');
      const declineFlg = get(clientSCDPF, 'declineFlg', '');
      const checkEligible =  (claimFlg === 'Y' || substdFlg === 'Y' || postponeFlg === 'Y' || declineFlg === 'Y');
      if (checkEligible) {
        gotoContact();
        return false;
      }
      return true
      // const checkedLimit = checkLimit(clientSAInfo);

    } catch (error) {
      console.error(error);
      return true; // bypass
    }

  }, [personalDetails, sqsQuotation, gotoContact]);

  const onEdit = () => {
    history.push('./coverage');
  }

  // const addMessage = (msg: string) => {
  //   console.log('addMessage/msg', msg, message.length, message === '');
  //   if (message.length === 0) {
  //     console.log('IS SET MESSAGE')
  //     setMessage(msg);
  //   } else {
  //     console.log('IS ADD MESSAGE')
  //     setMessageAdd1(msg);
  //   }
  // };


  const checkMinSumAssured = useCallback(async () => {
    dispatch(setLoadingState(true));
    const nric = get(personalDetails, 'nric', '');
    const payload = { nric, data: { ...quoteRawResponse } };
    //return true;
    try {
      const response = await postCalculateMinAssuredAndFlag(payload);
      const data = get(response, 'data');

      const basicStatusValid = get(data, 'basic.status', 'Success') === 'Success';
      const riderCIStatusValid = get(data, 'riderCI.status', 'Success') === 'Success';
      const riderPAStatusValid = get(data, 'riderPA.status', 'Success') === 'Success';
      console.debug('checkMinSumAssured/status', basicStatusValid, riderCIStatusValid, riderPAStatusValid);
      dispatch(setLoadingState(false));
      setMessage('');
      setMessageAdd1('');
      setMessageAdd2('');
      
      // return true;
      if (!basicStatusValid) {
        const noLimit = get(data, 'basic.message', '') === 'No Limit';
        const remainLimit = get(data, 'basic.message', '') === 'Remaining Limit';
        const balance = get(data, 'basic.balance', 0);
        if (remainLimit) {
          setMessage(`You can only purchase Basic Plan sum assured of up to ${Helpers.toCurrency(balance).slice(0, -3)} for this application. Please go back to the quotation page to amend the sum assured.`);
        }
        if (noLimit) {
          gotoContact();
        }
      }
      if (!riderCIStatusValid) {
        console.log('riderCIStatusValid');
        const noLimit = get(data, 'riderCI.message', '') === 'No Limit';
        const remainLimit = get(data, 'riderCI.message', '') === 'Remaining Limit';
        const balance = get(data, 'riderCI.balance', 0);
        let msg = '';
        if (remainLimit) {
          msg = `You can only purchase Critical Illiness Cover sum assured of up to ${Helpers.toCurrency(balance).slice(0, -3)} for this application. Please go back to the quotation page to amend the sum assured.`;
        }
        if (noLimit) {
          msg = `You've have reached the maximum sum assured limit allowed for Critical Illiness Cover. Please go back to the quotation page to remove Critical Illness Cover (2025) and continue with the Basic Plan.`;
        }
        if (basicStatusValid) {
          setMessage(msg);
        } else {
          setMessageAdd1(msg);
        }
      }
      if (!riderPAStatusValid) {
        const noLimit = get(data, 'riderPA.message', '') === 'No Limit';
        const remainLimit = get(data, 'riderPA.message', '') === 'Remaining Limit';
        const balance = get(data, 'riderPA.balance', 0);
        let msg = '';
        if (remainLimit) {
          msg = `You can only purchase Personal Accident Cover sum assured of up to ${Helpers.toCurrency(balance).slice(0, -3)} for this application. Please go back to the quotation page to amend the sum assured.`;
        }
        if (noLimit) {
          msg = `You've have reached the maximum sum assured limit allowed for Personal Accident Cover. Please go back to the quotation page to remove Personal Accident Cover and continue with the Basic Plan.`;
        }
        if (basicStatusValid && riderCIStatusValid) {
          setMessage(msg);
        } else if (!basicStatusValid && !riderCIStatusValid) {
          setMessageAdd2(msg);
        } else {
          setMessageAdd1(msg);
        }
      }
      if (!basicStatusValid || !riderCIStatusValid || !riderPAStatusValid) {
        setIsShowPopup(true);
        return false;
      }

      dispatch(setLoadingState(false));
      const bypassMin = urlParams.get('min');
      
      if (bypassMin === 'true') {
        return true;
      }
      //return data === 'true';
      return true; //temp bypass until actual api response create
    } catch (error) {
      dispatch(setLoadingState(false));
      return true; //bypass
    }
  }, [dispatch, quoteRawResponse, gotoContact, setMessage, setMessageAdd1, setMessageAdd2, personalDetails, urlParams]);

  const checkBmi = useCallback(async () => {
    const { weight, height } = healthValues;
    const dob = personalDetails?.dob;
    const age = Helpers.getAge(new Date(dob || ''));
    try {
      const payload = new BMICheckRequest({ weight, height, age, prodcode: 'SIO' });
      const response = await postBMICheck(payload);
      const status = get(response, 'data.status', '');
      console.debug('postBMICheck', response, status);
      return lowerCase(status) !== 'decline';
    } catch (error) {
      console.error(error);
      return true;
    }
   
  }, [healthValues, personalDetails])

  const onClickContinue = () => {
    // checkMinSumAssured();
    if (step < maxStep) {
      dispatch(completeStep(true));
      dispatch(updateIncome(values));
      trackFormSubmmit();
      const next = step + 1;
      trackOnload(next);
      setStep(next);
      history.push(`/sio/about-you?step=${next}`)
      Helpers.scrollToTop();
    }
  }

  const onBack = () => {
    if (step === minStep) {
      history.push('/sio/my-info?step=2')
    } else {
      const back = step - 1;
      trackOnload(back);
      setStep(back);
      history.push(`/sio/about-you?step=${back}`)
      Helpers.scrollToTop();
    }
  }

  const checkFulfill = useCallback(async () => {
    dispatch(setLoadingState(true));
    if (typeUW === 'SIOB') {
      if (needToGoToContact()) {
        gotoContact();
        dispatch(setLoadingState(false));
        return;
      }
      if (!await checkBmi()) {
        gotoContact();
        dispatch(setLoadingState(false));
        return;
      }
    }
    
    if (!await checkMaxSumAssuredAndFlag()) {
      dispatch(setLoadingState(false));
      return;
    }

    if (!await checkMinSumAssured()) {
      dispatch(setLoadingState(false));
      return;
    }
    trackOnload(step);
    trackFormSubmmit();
    await updateMyInfoAndContinue();
  }, [checkBmi, step, checkMaxSumAssuredAndFlag, typeUW, checkMinSumAssured, updateMyInfoAndContinue, dispatch, gotoContact, needToGoToContact]);


  const onProceed = (fulfil: boolean) => {
    if (typeUW === 'SIOB') {
      dispatch(updateHealth({ ...healthValues, isSmoker }));
    }
    if (fulfil) {
      checkFulfill();
    } else {
      gotoContact();
    }
  }

  const onFocusOnOthers = (type: 'fund' | 'wealth'): void => {
    const currentValue = values[type];
    let nextValue;
    if (!currentValue) {
      nextValue = ['other'];
      patchValue({ [type]: nextValue });
    } else {
      if (!includes(currentValue, 'other') && Array.isArray(currentValue)) {
        nextValue = [ ...currentValue, 'other' ];
        patchValue({ [type]: nextValue });
      }
    }
    console.debug(currentValue, values, type, nextValue);

  }

  const initStep = useCallback(() => {
    const step = urlParams.get('step') || 0;
    setStep(Number(step));
    trackOnload(step);
  }, [urlParams, setStep])

  useEffect(() => {
    const quotation: SQSPocketResponse = new SQSPocketResponse(sqsQuotation);
    const illnessCoverPlan: Coverages | undefined = find(quotation.coverages, { id: 'lateStageAddCICover' });

    const smoker = get(basicDetials, 'smoker', 'N');
    const isSmorking = smoker === 'Y';
    if (isSmoker === undefined) {
      setIsSmoker(isSmorking);
      patchHealthValue({ isSmoker: isSmorking });
    }

    if (isAppliedCI === undefined) {
      const hasCI = illnessCoverPlan ? true : false;
      setIsAppliedCI(hasCI);
      patchHealthValue({ isAppliedCI: hasCI });
    }
    console.debug('isAppliedCI', isAppliedCI);
    if (!isPageLoaded) {
      if (!isStep2Completed && process.env.REACT_APP_INJECT_CHECK !== 'true') {
        dispatch(completeStep(false));
        
        history.push('/sio/my-info');
      } else {
        initStep();
        setIsPageLoaded(true);
      }
    }
    console.debug('typeUW', typeUW);
  }, [basicDetials, isSmoker, patchHealthValue, dispatch, typeUW, history, isStep2Completed, setIsSmoker, isAppliedCI, setIsAppliedCI, sqsQuotation, step, initStep, isPageLoaded, setIsPageLoaded])

  const renderCheckboxes = (sources: any[], type: string) => sources.map((source, index: number) => {
    return (<Checkbox key={`${index}`} variant="border" checked={includes(values[type], source.value)} onChange={onChangeCheckBox(source, type)}><Text>{source.label}</Text></Checkbox>)
  });


  return (
    <Container>
      <ContainerGrid>
        <FullGrid>
          <MultiSteps steps={5} step={3} label={`${step === 0 ? 'Income questions' : 'Health questions'}`} />
          <Heading margin="0 0 30px"> More about yourself </Heading>
        </FullGrid>
        <LeftGrid>

          {step === 0 && (
            <React.Fragment>
              <Heading variant="label"> Exact duties of occupation </Heading>
              {!errors?.duties?.isValid && errors?.duties?.message && <Bullet variant="error">{errors?.duties?.message}</Bullet>}
              <Input margin="0 0 30px" name="duties" invalid={!isEmpty(errors?.duties?.message)} maxLength={98} value={values['duties']} onChange={handleChange} />
              <Heading variant="label"> Nature of employer&apos;s business </Heading>
              <Dropdown placeholder="Please select" source={businesses} bindId="code" value={values['nature']} bindLabel="description" name="nature" onChange={handleChange} />
              {values['nature'] === '12' && (
                <React.Fragment>
                  <Heading variant="label"> Others </Heading>
                  {!errors?.otherBusiness?.isValid && errors?.otherBusiness?.message && <Bullet variant="error">{errors?.otherBusiness?.message}</Bullet>}
                  <Input margin="0 0 30px" placeholder="Please specify" name="otherBusiness" invalid={!isEmpty(errors?.otherBusiness?.message)} maxLength={98} value={values['otherBusiness']} onChange={handleChange} />
                </React.Fragment>)
              }
              <Heading variant="label"> Please select your source of funds (Origin of the funds used to pay your premiums) </Heading>
              <CheckboxList>
                {renderCheckboxes(sourceOfFunds, 'fund')}
                {!errors?.otherFund?.isValid && errors?.otherFund?.message && <Bullet variant="error">{errors?.otherFund?.message}</Bullet>}
                <Grid columns={5}>
                  <Cell width={2}><Checkbox variant="border" checked={includes(values['fund'], 'other')} onChange={onChangeCheckBox({ value: 'other' }, 'fund')}><Text>Others</Text></Checkbox></Cell>
                  <Cell width={3}><Input maxLength={50} name="otherFund" invalid={!isEmpty(errors?.otherFund?.message)} onFocus={() => onFocusOnOthers('fund')} placeholder="Please specify" onChange={handleChange} value={values['otherFund']} /></Cell>
                </Grid>
              </CheckboxList>
              <Heading variant="label"> Please select your source of wealth (Where your wealth is derived from) </Heading>
              <CheckboxList>
                {renderCheckboxes(sourceOfWealths, 'wealth')}

                {!errors?.otherWealth?.isValid && errors?.otherWealth?.message && <Bullet variant="error">{errors?.otherWealth?.message}</Bullet>}
                <Grid columns={5}>
                  <Cell width={2}><Checkbox variant="border" checked={includes(values['wealth'], 'other')} onChange={onChangeCheckBox({ value: 'other' }, 'wealth')}><Text>Others</Text></Checkbox></Cell>
                  <Cell width={3}><Input maxLength={50} name="otherWealth" invalid={!isEmpty(errors?.otherWealth?.message)} onFocus={() => onFocusOnOthers('wealth')}  placeholder="Please specify" onChange={handleChange} value={values['otherWealth']} /></Cell>
                </Grid>
              </CheckboxList>
              <Heading variant="label"> Your annual income </Heading>
              {!errors?.income?.isValid && errors?.income?.message && <Bullet variant="error">{errors?.income?.message}</Bullet>}
              <Input variant="prefix" type="number" name="income" maxLength={17} invalid={!isEmpty(errors?.income?.message)} value={values['income']} onChange={handleChange} boxLabel="S$" margin="0 0 40px"></Input>
              <Button
                label="Continue"
                onClick={onClickContinue}
                margin="0 0 20px"
                disabled={!formValid}
              />
            </React.Fragment>
          )}
          {step === 1 && typeUW === 'SIOB' && (
            <React.Fragment>
              <Heading variant="sub" margin="0 0 15px" fontWeight={600}>Smoker status</Heading>
              <Divider margin="0 0 30px" />
              <ContainerBlock background={theme.colors.grey[200]} padding="18px 30px" paddingTablet="25px 30px" margin={isSmoker ? "0 0 30px" : "0 0 40px"} marginTablet={isSmoker ? "0 0 30px" : "0 0 60px"}>
                <SmokerStatusContainer>
                  <Text fontWeight={600}>Are you a smoker?</Text>
                  <Text fontWeight={300} fontSize={theme.fontSize.bigSub} color={theme.colors.grey[300]}>{isSmoker ? 'Yes' : 'No'}</Text>
                </SmokerStatusContainer>
              </ContainerBlock>
              {isSmoker && (
                <React.Fragment>
                  <Heading variant="label" margin="0 0 10px">You have declared that you are a smoker, how many sticks do you smoke daily?</Heading>
                  <Text margin="0 0 15px"> Including social smokers, cigar smokers or those who have given up within the last 12 months. </Text>
                  <RadioButtonGroup margin="0 0 40px" marginTablet="0 0 60px" value={healthValues['isLessThan40']} name="isLessThan40" onChange={handleChangeHealth} className={customRadioButton}>
                    <RadioButton text="Less than 40 sticks" value="Y" />
                    <RadioButton text="40 sticks and above" value="N" />
                  </RadioButtonGroup>
                </React.Fragment>
              )}
              <Heading variant="sub">Other health questions</Heading>
              <Divider margin="0 0 30px" />

              <Heading variant="label" margin="0 0 15px">Height</Heading>
              {!healthErrors?.height?.isValid && healthErrors?.height?.message && (<Bullet variant="error">{healthErrors?.height?.message}</Bullet>)}
              <Input variant="suffix" boxLabel="cm" invalid={!isEmpty(healthErrors?.height?.message)} maxLength={3} type="numeric" name="height" value={healthValues['height']} onChange={handleChangeHealth}></Input>

              <Heading variant="label" margin="0 0 15px">Weight</Heading>
              {!healthErrors?.weight?.isValid && healthErrors?.weight?.message && (<Bullet variant="error">{healthErrors?.weight?.message}</Bullet>)}
              <Input variant="suffix" boxLabel="kg" maxLength={3} type="numeric" invalid={!isEmpty(healthErrors?.weight?.message)} name="weight" value={healthValues['weight']} onChange={handleChangeHealth}></Input>

              <Heading variant="label" margin="0 0 12px">Have you had:</Heading>
              <BulletList bottomMargin="small">
                <Bullet>cancer or carcinoma in situ</Bullet>
                <Bullet>chest pain, heart attack or coronary heart disease</Bullet>
                <Bullet>stroke/ transient ischaemic attack</Bullet>
                <Bullet>diabetes</Bullet>
                <Bullet>chronic kidney disease</Bullet>
                <Bullet>arthritis</Bullet>
              </BulletList>
              <RadioButtonGroup value={healthValues['q1']} name="q1" onChange={handleChangeHealth} >
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              <Heading variant="label" margin="0 0 12px">In the past 5 years, have you had:</Heading>
              <BulletList bottomMargin="small">
                <Bullet>blood disorder</Bullet>
                <Bullet>mitral valve prolapse</Bullet>
                <Bullet>hepatitis B</Bullet>
                <Bullet>high blood pressure</Bullet>
                <Bullet>raised cholesterol</Bullet>
                <Bullet>thyroid disorder</Bullet>
              </BulletList>
              <RadioButtonGroup value={healthValues['q2']} name="q2" onChange={handleChangeHealth} >
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              <Heading variant="label" margin="0 0 12px">Have you ever had any health conditions which led up to:</Heading>
              <BulletList bottomMargin="small">
                <Bullet>more than 10 consecutive days off work, or</Bullet>
                <Bullet>follow-up consultations lasting a month or more, or</Bullet>
                <Bullet>treatment for 1 month or more?</Bullet>
              </BulletList>
              <RadioButtonGroup value={healthValues['q3']} name="q3" onChange={handleChangeHealth} >
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              <Heading variant="label" margin="0 0 12px">Have you:</Heading>
              <BulletList bottomMargin="small">
                <Bullet>had any abnormal medical investigations, tests or scans, or</Bullet>
                <Bullet>been awaiting for any pending medical investigations, tests or scans, or</Bullet>
                <Bullet>had any symptoms for which you intend to or been advised to seek medical advice consultation or investigation?</Bullet>
              </BulletList>
              <RadioButtonGroup value={healthValues['q4']} name="q4" onChange={handleChangeHealth}>
                <RadioButton text="Yes" value="Y" />
                <RadioButton text="No" value="N" />
              </RadioButtonGroup>

              {isAppliedCI && (
                <React.Fragment>
                  <Heading variant="label" margin="0 0 12px">Have any of your natural parents, sisters or brothers died from or had breast, ovarian and/or colon cancer before the age of 65?</Heading>
                  <RadioButtonGroup value={healthValues['q5']} name="q5" onChange={handleChangeHealth}>
                    <RadioButton text="Yes" value="Y" />
                    <RadioButton text="No" value="N" />
                  </RadioButtonGroup>
                </React.Fragment>
              )}

            </React.Fragment>
          )}
          {step === 1 && typeUW === 'SIOB' && (
            <React.Fragment>
              <Heading variant="sub" margin="0 0 15px" fontWeight={600}>Financial declaration</Heading>
              <Divider margin="0 0 30px" />
              <BulletList margin="0 0 30px">
                <Bullet>Where your total* life insurance coverage exceeds S$1 million, you have calculated that the total* is within 20 times of your current annual income.</Bullet>
              </BulletList>
              <Heading variant="desc" margin="0 0 40px">* Including coverage with other insurance companies and insurance applications that are pending approvals.</Heading>
              <Button onClick={() => { onProceed(true) }} disabled={!healthFormValid}  label="I fulfil the above criteria" margin="0 0 20px" />
              <Button variant="secondary" onClick={() => { onProceed(false) }} label="I do not fulfil the above criteria" margin="0 0 20px" />
            </React.Fragment>
          )}
          {step === 1 && typeUW === 'SIOA1' && (
            <React.Fragment>
              <Heading variant="sub" margin="0 0 15px" fontWeight={600}>I declare the following</Heading>
              <Divider margin="0 0 30px" />
              <BulletList margin="0 0 30px">
                <Bullet>I have not been referred to or consulting any specialist medical practitioner (other than pregnancy related), or waiting for the results of any test or investigations.</Bullet>
                {isAppliedCI && (<Bullet>I confirm that none of my natural parents, sisters or brothers has been diagnosed with or died from either breast, ovarian and/or colon cancer before attaining the age of 65.</Bullet>)}
                <Bullet>Where my total* life insurance coverage exceeds S$1 million, I have calculated that the total* is within 20 times of my current annual income.</Bullet>
              </BulletList>
              <Heading variant="desc" margin="0 0 40px">* Including coverage with other insurance companies and insurance applications that are pending approvals.</Heading>
              <ContainerBlock background={theme.colors.grey[200]} padding={20} paddingTablet="20px 30px" margin="0 0 40px">
                <Text margin="0 0 10px" fontWeight={600}>Note: I understand that</Text>
                <BulletList variant="default" margin="0">
                  <Bullet className={smallMargin}>this simplified offer is being made based on disclosures I made in prior applications for Singapore Life Ltd. insurance policies; and </Bullet>
                  <Bullet className={largeMargin}>if any material fact was not fully disclosed in such prior applications, any policy issued pursuant to this application may be void.</Bullet>
                </BulletList>
              </ContainerBlock>
              <Button onClick={() => { onProceed(true) }} label="I fulfil the above criteria" margin="0 0 20px" />
              <Button variant="secondary" onClick={() => { onProceed(false) }} label="I do not fulfil the above criteria" margin="0 0 20px" />
            </React.Fragment>
          )}
          {step === 1 && typeUW === 'SIOA3' && (
            <React.Fragment>
              <Heading variant="sub" margin="0 0 15px" fontWeight={600}>I declare the following</Heading>
              <Divider margin="0 0 30px" />
              <BulletList margin="0 0 30px">
                <Bullet>I do not have and have not been advised to seek medical treatment for any health conditions (other than for minor ailments) including cancer, tumour/lump, diabetes, hypertension, HIV/AIDS, heart disease, stroke/transient ischemic attack, any blood disorder, drug and/or alcohol habits. I am not currently seeking, receiving nor do I intend to seek or receive any advice/treatment.</Bullet>
                <Bullet>For the past 5 years, I have not been admitted to any hospital nor have I undergone any medical investigations or tests which were abnormal (other than for minor ailments).</Bullet>
                <Bullet>I do not engage or intend to engage in any hazardous activities for leisure or for work.</Bullet>
                {isAppliedCI && (<Bullet>I confirm that none of my natural parents, sisters or brothers has been diagnosed with or died from either breast, ovarian and/or colon cancer before attaining the age of 65.</Bullet>)}
                <Bullet>Where my total* life insurance coverage exceeds S$1 million, I have calculated that the total* is within 20 times of my current annual income.</Bullet>
              </BulletList>
              <Heading variant="desc" margin="0 0 40px">* Including coverage with other insurance companies and insurance applications that are pending approvals.</Heading>
              <ContainerBlock background={theme.colors.grey[200]} padding={20} paddingTablet="20px 30px" margin="0 0 40px">
                <Text margin="0 0 10px" fontWeight={600}>Note: I understand that</Text>
                <BulletList variant="default" margin="0">
                  <Bullet className={smallMargin}>this simplified offer is being made based on disclosures I made in prior applications for Singapore Life Ltd. insurance policies; and </Bullet>
                  <Bullet className={largeMargin}>if any material fact was not fully disclosed in such prior applications, any policy issued pursuant to this application may be void.</Bullet>
                </BulletList>
              </ContainerBlock>
              <Button onClick={() => { onProceed(true) }} label="I fulfil the above criteria" margin="0 0 20px" />
              <Button variant="secondary" onClick={() => { onProceed(false) }} label="I do not fulfil the above criteria" margin="0 0 20px" />
            </React.Fragment>
          )}
          <Button onClick={onBack} variant="secondary" label="Back" margin="0 0 40px" />
          <Modal show={isShowPopup} onClose={togglePopup}>
            <Heading variant="sub" > Maximum sum assured limit exceeded </Heading>
            <Text margin="0 0 20px">{message}</Text>
            <Text margin="0 0 20px">{messageAdd1}</Text>
            <Text margin="0 0 20px">{messageAdd2}</Text>
            <Text margin="0 0 40px">Alternatively, you may leave your contact details <Link variant="button" text="here" onClick={() => gotoContact()} /> and our financial adviser representative will be in touch with you shortly </Text>
            <Button onClick={() => { onEdit() }} label="Proceed to edit"></Button>
          </Modal>
        </LeftGrid>
        <EmptyGrid></EmptyGrid>
        <RightGrid>
          <ContactBox items={contactInformation} />
        </RightGrid>
      </ContainerGrid>


    </Container>
  );
};

export default AboutYou;
